import React, {useEffect, useMemo} from 'react';
import {Button, Text, TextInput} from '@/ui-kits';
import {useDispatch, useSelector} from 'react-redux';
import {patientAccountExportRequest} from '@/services/redux/slices';
import {useFormik} from 'formik';
import {authDataSelector, accountExportStatusSelector, accountDeletionStatusSelector, isAccountExportLoadingSelector} from '@/services/redux/selectors';
import {DataExportValidation} from '@/utils/schema';
import {PatientAccountTicketRequestStatus} from '@/constants';

interface initialValuesType {
  email: string;
  reason: string;
}
const initialValues: initialValuesType = {
  email: '',
  reason: '',
};
export const DataExport: React.FC = () => {
  // ===========================
  // hooks
  // ===========================
  const dispatch = useDispatch();
  const {values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isValid} = useFormik({
    initialValues,
    onSubmit,
    validationSchema: DataExportValidation,
  });

  // ===========================
  // selectors
  // ===========================
  const authdata = useSelector(authDataSelector);
  const accountExportStatus = useSelector(accountExportStatusSelector);
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);
  const loading = useSelector(isAccountExportLoadingSelector);

  // ===========================
  // state
  // ===========================
  const disableConfirmButton = useMemo(() => {
    if (!isValid) return true;
    if (accountExportStatus === PatientAccountTicketRequestStatus.Requested) return true;
    if ([
      PatientAccountTicketRequestStatus.Pending,
      PatientAccountTicketRequestStatus.Paused,
      PatientAccountTicketRequestStatus.Resumed,
      PatientAccountTicketRequestStatus.Approved,
    ].includes(accountDeletionStatus)) return true;
    return false;
  }, [isValid, accountExportStatus, accountDeletionStatus]);

  // ===========================
  // functions
  // ===========================
  function onSubmit(values: initialValuesType) {
    dispatch(patientAccountExportRequest({contact_email: values.email, reason: values.reason}));
    setFieldValue('reason', '');
  }

  function handleConfirmExport() {
    if (loading) return;
    handleSubmit();
  }
  
  
  useEffect(() => {
    if (authdata) {
      setFieldValue('email', authdata?.email_id);
    }
  }, []);

  return (
    <>
      <div className="border rounded-xl border-gray-400 md:p-6 p-4 mt-6">
        <Text variant="title" className="font-FigtreeSemiBold text-ui_blue">
          Export my data
        </Text>
        <Text variant="small" className="text-gray-500 mt-1">
          Download the copy of your health consultation history
        </Text>
        <div className="mt-5 border rounded-md border-gray-300 p-3 bg-ui_input_border_dark/5">
          <h5 className="text-ui_blue text-sm">
            <span className="font-bold">Note :</span> We will prepare a ZIP file containing all your account data, including your profile information,
            consultation history, and uploaded files.
          </h5>
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 md:gap-6">
          <TextInput
            value={values.email}
            placeholder="Enter Your email"
            labelClassName="!font-FigtreeSemiBold !text-lg"
            disabled={true}
            error={errors.email}
            touched={touched.email}
            label="Contact Email"
            required
            wrapperClassName="border"
            onChangeText={handleChange('email')}
            onBlur={handleBlur('email')}
            className="w-full md:w-[90%]"
            isHoverable={true}
          />

          <TextInput
            value={values.reason}
            placeholder={`Let us know why you're exporting your data`}
            labelClassName="!font-FigtreeSemiBold !text-lg"
            className="mt-4 sm:mt-0"
            label="Reason"
            showOptionalLabel={true}
            wrapperClassName="border"
            inputClassName="max-h-[60px]"
            multiline={true}
            onChangeText={handleChange('reason')}
          />
        </div>
        <Button
          className="mt-5 mb-3 sm:my-3 w-[217px] "
          title="Confirm Export"
          onClick={handleConfirmExport}
          isLoading={loading}
          disabled={disableConfirmButton}
        />
      </div>
    </>
  );
};
